import React, {useCallback, useEffect, useState} from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Button from "../common/button/Button";
import moment from "moment-timezone";
import {numberToWords} from "../Utility";
import RobotoRegular from "../../assets/fonts/Roboto-Regular";
import {logoBase64} from "../Utility";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";

const timezone = "Asia/Kolkata";
const ManualInvoice = ({invoice, detail = false, pdfEdit = false, pagination = {}}) => {
    const [pdfUrl, setPdfUrl] = useState(null);
    const navigator = useNavigate();
    const {address, restaurantName} = useSelector((state) => state.auth.selectedRole);

    const generatePdf = useCallback(async (download = false) => {
        if (!invoice) return;

        const doc = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
        });

        doc.addFileToVFS("Roboto-Regular.ttf", RobotoRegular);
        doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");

        const pageWidth = doc.internal.pageSize.getWidth();

        // Logo
        doc.addImage(logoBase64, "PNG", 10, 10, 40, 20);

        // Title
        doc.setFontSize(18);
        doc.text("Invoice", pageWidth / 2, 20, {align: "center"});

        // CreateInvoice No
        doc.setFontSize(11);
        doc.text(`INVOICE NO: ${invoice?.invoiceNo}`, pageWidth / 2, 28, {
            align: "center",
        });

        doc.setFontSize(9);
        doc.text(
            `(INVOICE PERIOD: ${moment(invoice?.startDate).tz(timezone).format("MMM DD, YYYY")}` +
            `${invoice.endDate ? ` - ${moment(invoice?.endDate).tz(timezone).format("MMM DD, YYYY")}` : ""})`,
            pageWidth / 2,
            32,
            {align: "center"}
        );


        //Invoice date
        doc.setFontSize(9);
        doc.text(
            `INVOICE DATE: ${moment(invoice?.createdAt).tz(timezone).format("MMM DD, YYYY")}`,
            pageWidth - 34,
            32,
            {align: "center"}
        );

        //Vibe24 Cafe Details
        const maxWidth = 100;
        const startX = 14;
        let currentCafeY = 50;

        doc.setFont("helvetica", "bold");
        // Restaurant Name
        if (restaurantName) {
            const streetLines = doc.splitTextToSize(restaurantName, maxWidth);
            doc.text(streetLines, startX, currentCafeY);
            currentCafeY += streetLines.length * 4; // Line height
        }

        doc.setFont("helvetica", "normal");

        // Handle Street Line
        if (address?.street) {
            const streetLines = doc.splitTextToSize(address.street, maxWidth);
            doc.text(streetLines, startX, currentCafeY);
            currentCafeY += streetLines.length * 4; // Line height
        }

        // Combine remaining fields into one string
        const restAddress = [
            address?.city,
            address?.state,
            address?.country,
            address?.zipCode,
        ]
            .filter(Boolean)       // remove empty/null
            .join(', ');           // join in a single line

        if (restAddress) {
            const restLines = doc.splitTextToSize(restAddress, maxWidth);
            doc.text(restLines, startX, currentCafeY);
            currentCafeY += restLines.length * 4;
        }

        // Add GSTIN
        if (invoice?.gstNumber) {
            doc.text(`GSTIN NO: ${invoice.gstNumber}`, startX, currentCafeY + 1);
        }

        // Customer Details (Right);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        const rightX = pageWidth - 65;
        doc.text(`Customer :`, rightX, 50);

        doc.setFont("helvetica", "normal");
        let currentY = 55;           // starting Y (customer name starts here)
        const lineHeight = 4.1; // change to any value you like (e.g., 4.5 or 6)
        const bottomMargin = 5; // keep your existing value

        const customerFields = [
            {label: "", value: invoice.customer?.name?.toUpperCase(), isAddress: true},
            {label: "PHONE", value: invoice.customer?.phone},
            {label: "GSTIN NO", value: invoice.customer?.gstNumber},
            {label: "ADDRESS", value: invoice.customer?.address, isAddress: true},
        ];

        for (const field of customerFields) {
            const rightMargin = 14;
            if (!field.value) continue;

            if (field.isAddress || field.label === "") {
                const availableWidth = pageWidth - rightX - rightMargin;
                const text = field.label ? `${field.label}: ${field.value}` : field.value;
                const lines = doc.splitTextToSize(text, availableWidth);

                doc.text(lines, rightX, currentY);
                currentY += lines.length * lineHeight; //  use same line height
            } else {
                doc.text(`${field.label}: ${field.value}`, rightX, currentY);
                currentY += lineHeight; // exact same spacing
            }
        }

        // AFTER LOOP
        const minNextContentY = 90;
        let nextContentY = currentY + bottomMargin;
        nextContentY = Math.max(nextContentY, minNextContentY);

        // Build dynamic header
        const header = ["S.No.", "ITEM", "QUANTITY", "UNIT PRICE"];
        if (invoice.enableDisable?.sacCode) header.push("SAC CODE");
        if (invoice.enableDisable?.gstPrint) header.push("GST");
        header.push("TOTAL");

        const getGstRateWithPercent = s => (s?.match(/(\d+(\.\d+)?)%/)?.[1] || 0) + '%';
        // Build body dynamically
        const body = invoice.items.map((item, i) => {
            const row = [
                i + 1,
                item.name,
                item.quantity,
                item.unitPrice,
            ];

            if (invoice.enableDisable?.sacCode) row.push(item.sacCode);
            if (invoice.enableDisable?.gstPrint) row.push(getGstRateWithPercent(item.gst) || "0%");

            row.push(Number(item.total).toFixed(2));
            return row;
        });

        // Calculate total without GST
        const totalWithoutGST = invoice.items.reduce((sum, item) => {
            const baseAmount = Number(item.unitPrice) * Number(item.quantity);
            return sum + baseAmount;
        }, 0);


        // Build foot dynamically
        const foot = [
            (() => {
                const row = ["", "", "", ""]; // placeholders for first 4 columns
                if (invoice.enableDisable?.sacCode) row.push(""); // SAC placeholder
                if (invoice.enableDisable?.gstPrint) row.push(`₹ ${invoice.gstInfo?.GST?.toFixed(2) || "0.00"}`);
                row.push(`₹ ${invoice.totals?.subtotal?.toFixed(2) || "0.00"}`);
                return row;
            })()
        ];

        // Table of Items
        autoTable(doc, {
            startY: nextContentY,
            head: [header],
            body: body,
            foot: foot,
            theme: "grid",
            headStyles: {fillColor: [255, 204, 204], textColor: 0, fontSize: 10},
            footStyles: {
                fillColor: [240, 240, 240], font: "Roboto",
                fontStyle: "normal", textColor: 0, fontSize: 9
            },
            styles: {fontSize: 10},
        });

        // Totals
        doc.setFont("Roboto", "normal");
        const rightMargin = 15;
        let finalY = doc.lastAutoTable.finalY || 80;
        const textX = pageWidth - rightMargin;

        doc.setFontSize(10);

        // start vertical position
        let lineY = finalY + 10;

        //  SUBTOTAL
        doc.setTextColor(0, 0, 0);
        doc.text(
            `SUBTOTAL: ₹ ${totalWithoutGST?.toFixed(2) || "0.00"} /-`,
            textX,
            lineY,
            {align: "right"}
        );
        lineY += 5;

        // DISCOUNT
        doc.setTextColor(255, 99, 132);
        doc.text(
            `DISCOUNT: - ₹ ${invoice?.totals?.discount?.toFixed(2) || "0.00"} /-`,
            textX,
            lineY,
            {align: "right"}
        );
        lineY += 5;

        // SGST (only if exists)
        if (invoice.enableDisable?.gstPrint && invoice.gstInfo?.SGST) {
            doc.setTextColor(0, 0, 0);
            doc.text(
                `SGST: ₹ ${invoice.gstInfo.SGST.toFixed(2)} /-`,
                textX,
                lineY,
                {align: "right"}
            );
            lineY += 5;
        }

        //  CGST (only if exists)
        if (invoice.enableDisable?.gstPrint && invoice.gstInfo?.CGST) {
            doc.text(
                `CGST: ₹ ${invoice.gstInfo.CGST.toFixed(2)} /-`,
                textX,
                lineY,
                {align: "right"}
            );
            lineY += 5;
        }

        //  Divider (exactly under last printed line)
        doc.setDrawColor(0);
        doc.setLineWidth(0.3);
        doc.line(textX - 60, lineY -3, textX, lineY - 3);

        // GRAND TOTAL
        doc.setTextColor(0, 0, 0);
        doc.setFont("Roboto", "normal");
        doc.text(
            `GRAND TOTAL: ₹ ${invoice.totals?.total?.toFixed(2) || "0.00"} /-`,
            textX,
            lineY + 1,
            {align: "right"}
        );

        // Advance
        doc.setTextColor(52, 168, 83);
        doc.text(
            `ADVANCE PAYMENT: - ₹ ${invoice?.totals?.advancePaid?.toFixed(2) || "0.00"} /-`,
            textX,
            lineY+6,
            {align: "right"}
        );
        lineY += 5;

        //  Divider (exactly under last printed line)
        doc.setDrawColor(0);
        doc.setLineWidth(0.3);
        doc.line(textX - 60, lineY +4, textX, lineY + 4);

        // GRAND TOTAL
        doc.setTextColor(0, 0, 0);
        doc.setFont("Roboto", "normal");
        doc.text(
            `REMAINING TOTAL: ₹ ${(
                (invoice.totals?.subtotal || 0) -
                ((invoice.totals?.discount || 0) + (invoice.totals?.advancePaid || 0))
            ).toFixed(2)} /-`,
            textX,
            lineY + 8,
            { align: "right" }
        );


        //  Grand Total in words & figures next block
        doc.setFontSize(10);
        doc.text(
            `GRAND TOTAL (in Figures) ₹ : ${invoice.totals?.total?.toFixed(2) || "0.00"} /-`,
            14,
            lineY + 20
        );
        doc.text(
            `GRAND TOTAL (in Words): ${numberToWords(invoice.totals?.total?.toFixed(2) || "0.00")}`,
            14,
            lineY + 25
        );


        // Bank Details
        if (invoice.enableDisable?.bankPrint) {
            let currentY = finalY + 70; // starting Y for bank info

            doc.setFont("helvetica", "bold");
            doc.text(`Bank Details :`, 14, currentY);
            currentY += 5; // small spacing after heading

            doc.setFont("helvetica", "normal");

            const bankFields = [
                {label: "BANK NAME", value: invoice.bankInfo?.name},
                {label: "ACCOUNT HOLDER", value: invoice.bankInfo?.accountHolderName},
                {label: "ACCOUNT NUMBER", value: invoice.bankInfo?.accountNumber},
                {label: "IFSC CODE", value: invoice.bankInfo?.ifscCode},
                {label: "UPI ID", value: invoice.bankInfo?.upiId} // optional
            ];

            const lineHeight = 5;

            for (const field of bankFields) {
                if (!field.value) continue; // skip if not available
                doc.text(`${field.label}: ${field.value}`, 14, currentY);
                currentY += lineHeight;
            }

            // Bank address (wrap text if needed)
            if (invoice.bankInfo?.branchAddress) {
                const addressText = `BANK ADDRESS: ${invoice.bankInfo.branchAddress}`;
                const wrappedAddress = doc.splitTextToSize(addressText, 70);
                doc.text(wrappedAddress, 14, currentY);
            }
        }


        if (download) {
            // Direct download
            doc.save(`${invoice.customer?.name}-${invoice?.invoiceNo || "download"}.pdf`);
        } else {
            // Preview
            const blob = doc.output("blob");
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
        }
    }, [invoice, address, restaurantName]);

    // Auto-generate preview when invoice changes
    useEffect(() => {
        if (invoice) generatePdf(false);
    }, [invoice, generatePdf]);


    //on back button handler
    const onBackHandler = () => {
        navigator("..");
    };

    if (!invoice) return null;


    return (
        <>
            <div className="mt-2 d-flex gap-2 justify-content-end position-relative" style={{top: -50}}>
                <Button className="bg-primary bg-opacity-50" onClick={() => generatePdf(true)}>
                    Download Invoice
                </Button>

                {/*Show while creating*/}
                {!(detail || pdfEdit) && (
                    <Button className="bg-secondary bg-opacity-50" onClick={onBackHandler}>
                        Back
                    </Button>
                )}

                {/*Only show in case of detail and update*/}
                {(detail || pdfEdit) && (
                    <Button
                        to={`${pdfEdit ? "../../" : "../"}?page=${pagination.currentPage}&limit=${pagination.limit}`}
                        className="btn-sm bg-secondary bg-opacity-50"
                    >
                        Back
                    </Button>
                )}
            </div>
            <div>
                {pdfUrl && (
                    <iframe
                        src={pdfUrl}
                        width="100%"
                        height="700px"
                        title="CreateInvoice Preview"
                        style={{border: "1px solid #ccc", borderRadius: "8px"}}
                    />
                )}
            </div>
        </>
    );
};

export default ManualInvoice;
